import { useState, useRef, useEffect, useCallback, FormEvent, KeyboardEvent } from "react";
import {
  Send,
  X,
  Loader2,
  Bot,
  User,
  Square,
  Trash2,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Brain,
  CheckCircle2,
  Circle,
  Plus,
  Eye,
  EyeOff,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { agents as agentsApi, agentTasks, type Task } from "../lib/api";
import { useChat, type ChatMessage } from "../contexts/ChatContext";

const BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : "/api/v1";

interface ProgressStep {
  label: string;
  percent: number;
  done: boolean;
}

interface ChatPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function ChatPanel({ open, onClose }: ChatPanelProps) {
  const {
    pendingMessage,
    consumePendingMessage,
    sessions,
    activeSessionId,
    setActiveSessionId,
    deleteSession,
    startSession,
    updateSessionMessages: ctxUpdateMessages,
    agentList,
    setAgentList,
  } = useChat();

  // Get the active session
  const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null;

  // Messages are always derived from the active session in context (single source of truth)
  const messages = activeSession?.messages ?? [];

  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingThinking, setStreamingThinking] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingExpanded, setThinkingExpanded] = useState(false);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [verbose, setVerbose] = useState(() => localStorage.getItem("chat-verbose") === "1");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load agents on mount
  useEffect(() => {
    agentsApi.list().then((res) => {
      setAgentList(res.data);
    }).catch(() => {});
  }, [setAgentList]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent, streamingThinking, progressSteps]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Pick up programmatic messages from context
  useEffect(() => {
    if (open && pendingMessage && !sending) {
      const msg = consumePendingMessage();
      if (msg) {
        doSend(msg);
      }
    }
  }, [open, pendingMessage, sending]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }
  }, [dropdownOpen]);

  // Helper to update messages immutably through the context
  function updateMessages(updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) {
    if (!activeSessionId) return;
    ctxUpdateMessages(activeSessionId, updater);
  }

  async function doSend(text: string) {
    if (!text.trim() || sending) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      timestamp: new Date(),
    };

    const assistantMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "",
      thinking: "",
      timestamp: new Date(),
      streaming: true,
      progress: [],
    };

    updateMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    setStreamingContent("");
    setStreamingThinking("");
    setIsThinking(false);
    setThinkingExpanded(false);
    setProgressSteps([]);

    // Build history (last 10 messages for context)
    const allMessages = [...(activeSession?.messages ?? []), userMsg];
    const history = allMessages.slice(-10).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const body: Record<string, unknown> = {
        message: text,
        history: history.slice(0, -1),
      };
      if (activeSession?.agentId) {
        body.agentId = activeSession.agentId;
      }

      const res = await fetch(`${BASE}/claude/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Request failed" }));
        updateMessages((prev) => [
          ...prev,
          { ...assistantMsg, content: `Error: ${err.error ?? res.statusText}`, streaming: false },
        ]);
        setSending(false);
        return;
      }

      // Add streaming assistant message
      updateMessages((prev) => [...prev, assistantMsg]);

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let fullContent = "";
      let fullThinking = "";
      let steps: ProgressStep[] = [];
      let buffer = "";
      let currentEvent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
            continue;
          }
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              switch (currentEvent) {
                case "content":
                  if (typeof data.text === "string") {
                    fullContent += data.text;
                    setStreamingContent(fullContent);
                  }
                  break;
                case "thinking_start":
                  setIsThinking(true);
                  break;
                case "thinking":
                  if (typeof data.text === "string") {
                    fullThinking += data.text;
                    setStreamingThinking(fullThinking);
                  }
                  break;
                case "thinking_end":
                  setIsThinking(false);
                  break;
                case "progress":
                  if (data.step) {
                    const newStep: ProgressStep = {
                      label: data.step,
                      percent: data.percent ?? 0,
                      done: (data.percent ?? 0) >= 100,
                    };
                    // Mark previous steps as done
                    steps = steps.map((s) => ({ ...s, done: true }));
                    steps = [...steps, newStep];
                    setProgressSteps([...steps]);
                  }
                  break;
                case "error":
                  if (data.error) {
                    fullContent += `\n\nError: ${data.error}`;
                    setStreamingContent(fullContent);
                  }
                  break;
              }
            } catch {
              // ignore parse errors
            }
            currentEvent = "";
          }
        }
      }

      // Finalize
      updateMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsg.id
            ? {
                ...m,
                content: fullContent || "(No response)",
                thinking: fullThinking || undefined,
                streaming: false,
                progress: steps.length > 0 ? steps : undefined,
              }
            : m
        )
      );
    } catch (err: unknown) {
      if ((err as Error).name === "AbortError") {
        updateMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? { ...m, content: streamingContent || "(Stopped)", streaming: false }
              : m
          )
        );
      } else {
        const msg = err instanceof Error ? err.message : "Connection failed";
        updateMessages((prev) => [
          ...prev.filter((m) => m.id !== assistantMsg.id),
          { ...assistantMsg, content: `Error: ${msg}`, streaming: false },
        ]);
      }
    } finally {
      setSending(false);
      setStreamingContent("");
      setStreamingThinking("");
      setIsThinking(false);
      setProgressSteps([]);
      abortRef.current = null;
    }
  }

  async function handleSend(e?: FormEvent) {
    e?.preventDefault();
    doSend(input.trim());
  }

  function handleStop() {
    abortRef.current?.abort();
  }

  function handleClear() {
    updateMessages([]);
    setStreamingContent("");
    setStreamingThinking("");
    setProgressSteps([]);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape" && sending) {
      handleStop();
    }
  }

  function handleNewChat() {
    startSession({ title: "General", message: "" });
    // Don't send empty message — just create the session
    // Clear the pending message since we passed empty string
    consumePendingMessage();
    setDropdownOpen(false);
  }

  function handleNewAgentChat(agent: { _id: string; name: string }) {
    startSession({ title: agent.name, message: "", agentId: agent._id });
    consumePendingMessage();
    setDropdownOpen(false);
  }

  function handleSelectSession(sessionId: string) {
    setActiveSessionId(sessionId);
    setDropdownOpen(false);
  }

  function handleDeleteSession(e: React.MouseEvent, sessionId: string) {
    e.stopPropagation();
    deleteSession(sessionId);
    setDropdownOpen(false);
  }

  function formatTime(date: Date): string {
    const d = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  }

  // Derive display name for the header
  const headerTitle = activeSession?.title ?? "AI Assistant";
  const headerSubtitle = activeSession?.agentName ? `Agent: ${activeSession.agentName}` : undefined;

  // ─── Resizable width via drag ────────────────────────────
  const MIN_WIDTH = 400;
  const MAX_WIDTH = 900;
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem("chat-panel-width");
    return saved ? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Number(saved))) : 520;
  });
  const draggingRef = useRef(false);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;

    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      const delta = startX - ev.clientX; // dragging left = wider
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
      setPanelWidth(next);
    };

    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setPanelWidth((w) => {
        localStorage.setItem("chat-panel-width", String(w));
        return w;
      });
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [panelWidth]);

  if (!open) return null;

  return (
    <div className="flex flex-col h-screen bg-gray-900 border-l border-gray-800 relative shadow-2xl" style={{ width: panelWidth }}>
      {/* Drag handle (left edge) */}
      <div
        onMouseDown={handleDragStart}
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 hover:bg-brand-500/30 active:bg-brand-500/50 transition-colors"
      />
      {/* Header with session selector */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800 flex-shrink-0">
        <div className="relative flex-1 min-w-0" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 w-full px-2 py-1 rounded-md hover:bg-gray-800 transition-colors text-left"
          >
            <div className="w-5 h-5 rounded-md bg-brand-600/20 flex items-center justify-center flex-shrink-0">
              <Bot className="w-3 h-3 text-brand-400" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-sm font-semibold text-gray-100 truncate block">
                {headerTitle}
              </span>
              {headerSubtitle && (
                <span className="text-[10px] text-gray-500 truncate block">{headerSubtitle}</span>
              )}
            </div>
            <ChevronDown className="w-3 h-3 text-gray-500 flex-shrink-0 ml-auto" />
          </button>

          {dropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50 py-1 max-h-80 overflow-y-auto">
              {/* New Chat button */}
              <button
                onClick={handleNewChat}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-brand-400 hover:bg-gray-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="font-medium">New Chat</span>
              </button>

              {/* Sessions list */}
              {sessions.length > 0 && (
                <div className="border-t border-gray-700 mt-1 pt-1">
                  <p className="px-3 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                    Sessions
                  </p>
                  {sessions.map((session) => {
                    const isActive = session.id === activeSessionId;
                    const lastMsg = session.messages[session.messages.length - 1];
                    const hasActivity = session.messages.some((m) => m.streaming);
                    return (
                      <div
                        key={session.id}
                        onClick={() => handleSelectSession(session.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter") handleSelectSession(session.id); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors group cursor-pointer ${
                          isActive
                            ? "bg-brand-600/10 text-brand-400"
                            : "text-gray-300 hover:bg-gray-700"
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          <div className="w-5 h-5 rounded-md bg-gray-700 flex items-center justify-center">
                            {session.agentId ? (
                              <Bot className="w-3 h-3 text-gray-400" />
                            ) : (
                              <MessageSquare className="w-3 h-3 text-gray-400" />
                            )}
                          </div>
                          {hasActivity && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-400 animate-pulse border border-gray-800" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-xs">{session.title}</p>
                          <p className="text-[10px] text-gray-500 truncate">
                            {lastMsg
                              ? `${lastMsg.role === "user" ? "You" : "AI"}: ${lastMsg.content.slice(0, 40)}${lastMsg.content.length > 40 ? "..." : ""}`
                              : formatTime(session.createdAt)}
                          </p>
                        </div>
                        <button
                          onClick={(e) => handleDeleteSession(e, session.id)}
                          className="p-0.5 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                          title="Delete session"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Agents section */}
              {agentList.length > 0 && (
                <div className="border-t border-gray-700 mt-1 pt-1">
                  <p className="px-3 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                    Start new session with...
                  </p>
                  {agentList.map((agent) => (
                    <button
                      key={agent._id}
                      onClick={() => handleNewAgentChat(agent)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                    >
                      <div className="w-5 h-5 rounded-md bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-3 h-3 text-gray-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-xs">{agent.name}</p>
                        <p className="text-[10px] text-gray-500 capitalize">
                          {agent.role.replace("_", " ")}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => {
            const next = !verbose;
            setVerbose(next);
            localStorage.setItem("chat-verbose", next ? "1" : "0");
          }}
          className={`p-1 rounded transition-colors ${
            verbose
              ? "bg-brand-600/20 text-brand-400 hover:bg-brand-600/30"
              : "text-gray-500 hover:bg-gray-800 hover:text-gray-300"
          }`}
          title={verbose ? "Hide agent detail" : "Show agent detail"}
        >
          {verbose ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
        {messages.length > 0 && (
          <button
            onClick={handleClear}
            className="p-1 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300 transition-colors"
            title="Clear chat"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300 transition-colors"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 scrollbar-thin">
        {messages.length === 0 && !sending && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-10 h-10 bg-brand-600/10 rounded-xl flex items-center justify-center mb-3">
              <MessageSquare className="w-5 h-5 text-brand-400" />
            </div>
            <p className="text-sm text-gray-400 mb-1">
              {headerTitle}
            </p>
            <p className="text-xs text-gray-600">
              {activeSession?.agentName
                ? `Chat session with ${activeSession.agentName}`
                : "Ask about your sites, generate content ideas, get SEO advice, or anything else."}
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className="space-y-1">
            {/* Progress steps (shown during and after streaming for assistant messages) */}
            {msg.role === "assistant" && msg.streaming && progressSteps.length > 0 && (
              <ProgressPanel steps={progressSteps} forceExpanded={verbose} />
            )}
            {msg.role === "assistant" && !msg.streaming && msg.progress && msg.progress.length > 0 && (
              <ProgressPanel steps={msg.progress} collapsed={!verbose} />
            )}

            {/* Thinking block (shown during streaming) */}
            {msg.role === "assistant" && msg.streaming && (isThinking || streamingThinking) && (
              <ThinkingBlock
                content={streamingThinking}
                isActive={isThinking}
                expanded={verbose || thinkingExpanded}
                onToggle={() => setThinkingExpanded(!thinkingExpanded)}
              />
            )}

            {/* Thinking block (shown after streaming, collapsed unless verbose) */}
            {msg.role === "assistant" && !msg.streaming && msg.thinking && (
              <ThinkingBlock content={msg.thinking} isActive={false} defaultExpanded={verbose} />
            )}

            {/* Message bubble */}
            <div className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : ""}`}>
              {msg.role === "assistant" && (
                <div className="w-7 h-7 rounded-md bg-brand-600/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="w-4 h-4 text-brand-400" />
                </div>
              )}
              <div
                className={`max-w-[90%] rounded-lg px-4 py-3 ${
                  msg.role === "user"
                    ? "bg-brand-600 text-white text-sm"
                    : "bg-gray-800 text-gray-200"
                }`}
              >
                {msg.streaming && !msg.content && !streamingContent ? (
                  verbose && isThinking && streamingThinking ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-purple-400">
                        <Brain className="w-3.5 h-3.5 animate-pulse" />
                        <span>{activeSession?.agentName ?? "AI"} is reasoning...</span>
                      </div>
                      <div className="text-xs text-purple-300/60 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto scrollbar-thin">
                        {streamingThinking.slice(-800)}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {sending
                        ? (isThinking
                            ? `${activeSession?.agentName ?? "AI"} is reasoning...`
                            : `${activeSession?.agentName ?? "AI"} is thinking...`)
                        : `Running ${activeSession?.title ?? "check"}...`}
                    </div>
                  )
                ) : msg.role === "assistant" ? (
                  <>
                    {(() => {
                      const content = msg.streaming ? streamingContent || "..." : msg.content;
                      const buildTaskId = extractBuildTaskId(content);
                      const cleanContent = buildTaskId ? stripBuildTaskMarker(content) : content;
                      return (
                        <>
                          <div className="chat-markdown prose prose-sm prose-invert max-w-none break-words
                            prose-headings:text-gray-100 prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-2
                            prose-h2:text-base prose-h3:text-sm
                            prose-p:text-[13px] prose-p:leading-relaxed prose-p:my-1.5
                            prose-li:text-[13px] prose-li:leading-relaxed
                            prose-strong:text-gray-100
                            prose-code:text-brand-300 prose-code:bg-gray-900 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs
                            prose-table:text-xs
                            prose-th:text-gray-400 prose-th:font-medium prose-th:px-2 prose-th:py-1
                            prose-td:px-2 prose-td:py-1 prose-td:border-gray-700
                          ">
                            <ReactMarkdown>{cleanContent}</ReactMarkdown>
                          </div>
                          {buildTaskId && <TaskProgressTracker taskId={buildTaskId} />}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <div className="whitespace-pre-wrap break-words text-sm">
                    {msg.content}
                  </div>
                )}
              </div>
              {msg.role === "user" && (
                <div className="w-7 h-7 rounded-md bg-gray-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-4 h-4 text-gray-300" />
                </div>
              )}
            </div>
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-800 p-3 flex-shrink-0">
        <form onSubmit={handleSend} className="flex gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                activeSession?.agentName
                  ? `Message ${activeSession.agentName}...`
                  : "Ask anything..."
              }
              rows={1}
              disabled={sending}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
              style={{ maxHeight: "120px" }}
            />
          </div>
          {sending ? (
            <button
              type="button"
              onClick={handleStop}
              className="p-2 bg-red-600/20 hover:bg-red-600/30 border border-red-600/30 text-red-400 rounded-lg transition-colors flex-shrink-0"
              title="Stop (Esc)"
            >
              <Square className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className="p-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-30 disabled:hover:bg-brand-600 text-white rounded-lg transition-colors flex-shrink-0"
              title="Send (Enter)"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
        </form>
        <p className="text-[10px] text-gray-600 mt-1.5 text-center">
          Shift+Enter for new line &middot; Esc to stop
        </p>
      </div>
    </div>
  );
}

// ─── Progress Panel ─────────────────────────────────────────

function ProgressPanel({ steps, collapsed: initialCollapsed, forceExpanded }: { steps: ProgressStep[]; collapsed?: boolean; forceExpanded?: boolean }) {
  const [collapsed, setCollapsed] = useState(initialCollapsed ?? false);
  const isCollapsed = forceExpanded ? false : collapsed;
  const currentPercent = steps.length > 0 ? steps[steps.length - 1]!.percent : 0;
  const isDone = currentPercent >= 100;

  return (
    <div className="mx-8 mb-1">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 w-full text-left"
      >
        <div className="flex-1 min-w-0">
          {/* Progress bar */}
          <div className="w-full bg-gray-800 rounded-full h-1 overflow-hidden">
            <div
              className={`h-1 rounded-full transition-all duration-500 ${isDone ? "bg-green-500" : "bg-brand-500"}`}
              style={{ width: `${currentPercent}%` }}
            />
          </div>
        </div>
        <span className="text-[10px] text-gray-500 flex-shrink-0 tabular-nums">
          {isDone ? (
            <CheckCircle2 className="w-3 h-3 text-green-500" />
          ) : (
            `${currentPercent}%`
          )}
        </span>
        <ChevronRight className={`w-3 h-3 text-gray-600 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
      </button>

      {!isCollapsed && (
        <div className="mt-1.5 space-y-0.5">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[10px]">
              {step.done ? (
                <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
              ) : i === steps.length - 1 ? (
                <Loader2 className="w-3 h-3 text-brand-400 animate-spin flex-shrink-0" />
              ) : (
                <Circle className="w-3 h-3 text-gray-600 flex-shrink-0" />
              )}
              <span className={step.done ? "text-gray-500" : "text-gray-400"}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Thinking Block ─────────────────────────────────────────

function ThinkingBlock({
  content,
  isActive,
  expanded: controlledExpanded,
  onToggle,
  defaultExpanded,
}: {
  content: string;
  isActive: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  defaultExpanded?: boolean;
}) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded ?? false);
  const expanded = controlledExpanded ?? internalExpanded;
  const toggle = onToggle ?? (() => setInternalExpanded(!internalExpanded));

  return (
    <div className="mx-8 mb-1">
      <button
        onClick={toggle}
        className="flex items-center gap-1.5 text-[11px] text-purple-400/80 hover:text-purple-400 transition-colors"
      >
        {isActive ? (
          <Brain className="w-3 h-3 animate-pulse" />
        ) : (
          <Brain className="w-3 h-3" />
        )}
        <span>{isActive ? "Thinking..." : "Thought process"}</span>
        <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && content && (
        <div className="mt-1 px-2.5 py-2 bg-purple-500/5 border border-purple-500/10 rounded-md max-h-40 overflow-y-auto scrollbar-thin">
          <p className="text-[11px] text-purple-300/70 whitespace-pre-wrap leading-relaxed">
            {content}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Build Task ID extractor ─────────────────────────────────

const BUILD_TASK_RE = /<!--\s*BUILD_TASK:(\w+)\s*-->/;

function extractBuildTaskId(content: string): string | null {
  const match = content.match(BUILD_TASK_RE);
  return match?.[1] ?? null;
}

function stripBuildTaskMarker(content: string): string {
  return content.replace(BUILD_TASK_RE, "").trim();
}

// ─── Task Progress Tracker ───────────────────────────────────

function TaskProgressTracker({ taskId }: { taskId: string }) {
  const [task, setTask] = useState<Task | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let interval: ReturnType<typeof setInterval>;

    async function poll() {
      try {
        const res = await agentTasks.get(taskId);
        if (cancelled) return;
        setTask(res.data);
        // Stop polling if terminal
        if (res.data.status === "done" || res.data.status === "failed") {
          clearInterval(interval);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load task");
        clearInterval(interval);
      }
    }

    poll(); // immediate
    interval = setInterval(poll, 3000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId]);

  if (error) {
    return (
      <div className="text-xs text-red-400 py-1">Failed to load progress: {error}</div>
    );
  }

  if (!task) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading task...
      </div>
    );
  }

  const steps = task.process ?? [];
  const isDone = task.status === "done";
  const isFailed = task.status === "failed";
  void steps.findIndex((s) => s.status === "in_progress"); // kept for future use
  const completedCount = steps.filter((s) => s.status === "done").length;
  const totalSteps = steps.length;
  const percent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  return (
    <div className="space-y-2 py-1">
      {/* Overall progress bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-gray-800 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-1.5 rounded-full transition-all duration-700 ${
              isFailed ? "bg-red-500" : isDone ? "bg-green-500" : "bg-brand-500"
            }`}
            style={{ width: `${isDone ? 100 : percent}%` }}
          />
        </div>
        <span className="text-[10px] text-gray-500 tabular-nums w-8 text-right">
          {isDone ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 inline" />
          ) : isFailed ? (
            <span className="text-red-400">Fail</span>
          ) : (
            `${percent}%`
          )}
        </span>
      </div>

      {/* Step list */}
      <div className="space-y-0.5">
        {steps.map((step, i) => {
          const isActive = step.status === "in_progress";
          const isStepDone = step.status === "done";

          return (
            <div key={i} className={`flex items-center gap-2 py-0.5 px-1 rounded ${isActive ? "bg-brand-500/5" : ""}`}>
              {/* Status icon */}
              <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                {isStepDone ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                ) : isActive ? (
                  <Loader2 className="w-3.5 h-3.5 text-brand-400 animate-spin" />
                ) : (
                  <Circle className="w-3 h-3 text-gray-700" />
                )}
              </div>

              {/* Step name */}
              <span className={`text-xs flex-1 ${
                isActive ? "text-brand-300 font-medium" :
                isStepDone ? "text-gray-500" :
                "text-gray-600"
              }`}>
                {step.name}
              </span>

              {/* Duration or active indicator */}
              {isActive && (
                <span className="text-[10px] text-brand-400/60 animate-pulse">running</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {isFailed && task.error && (
        <div className="text-xs text-red-400/80 bg-red-900/10 border border-red-800/20 rounded px-2 py-1.5 mt-1">
          {task.error}
        </div>
      )}

      {/* Done summary */}
      {isDone && task.result && (
        <div className="text-xs text-green-400/80 bg-green-900/10 border border-green-800/20 rounded px-2 py-1.5 mt-1">
          {typeof task.result["summary"] === "string" ? task.result["summary"] : "Build complete"}
        </div>
      )}
    </div>
  );
}
